<template>
  <div class="country-selector critical-component-hide">
    <button
      class="country-selector__close icon-button"
      type="button"
      :aria-label="$string('accessibility.close')"
      @click="handleCloseOverlay"
    >
      <slot name="close">
        <icon-close />
      </slot>
    </button>

    <!-- Body -->
    <div class="country-selector__body">
      <div
        v-if="!showPrompt"
        class="country-selector__form"
      >
        <div
          class="
            country-selector__title
            text-heading-2xl
            text-heading-4xl-desktop
          "
          v-text="$string('general.country_selector.selector_title')"
        />
        <div class="country-selector__form-group form-group">
          <label
            for="selected-language"
            v-text="$string('general.country_selector.selector_country_label')"
          />
          <select
            v-if="locales"
            id="selected-country"
            name="selected_country"
            @change="handleLocaleSelect"
          >
            <option
              v-for="country of locales"
              :key="`country-${country.isoCode}`"
              :value="country.isoCode"
              :aria-current="(getSelectedState(country.isoCode, selectedCountry.toUpperCase()))"
              :selected="(getSelectedState(country.isoCode, selectedCountry.toUpperCase()))"
            >
              {{ country.name }} ({{ country.currency.isoCode }} {{ country.currency.symbol }})
            </option>
          </select>
        </div>

        <div class="country-selector__form-group form-group">
          <label
            for="selected-language"
            v-text="$string('general.country_selector.selector_language_label')"
          />
          <select
            v-if="availableLanguages"
            id="selected-language"
            name="selected_language"
            @change="handleLocaleSelect"
          >
            <option
              v-for="language of availableLanguages"
              :key="`lang-${language.isoCode}`"
              :value="language.isoCode"
              :aria-current="(getSelectedState(language.isoCode, selectedLanguage.toUpperCase()))"
              :selected="(getSelectedState(language.isoCode, selectedLanguage.toUpperCase()))"
            >
              {{ language.endonymName }}
            </option>
          </select>
        </div>
      </div>

      <div
        v-else
        class="country-selector__prompt"
      >
        <div class="country-selector__prompt-header">
          <div
            class="
              country-selector__prompt-title
              text-heading-2xl
              text-heading-4xl-desktop
            "
            v-text="$string('general.country_selector.prompt_title',
                            { replace: { country: suggestedCountryNameFormatted }}
            )"
          />
        </div>

        <p
          v-if="$string('general.country_selector.prompt_suggestion') !== 'No string found'"
          class="country-selector__prompt-content-subtitle"
          v-text="$string('general.country_selector.prompt_suggestion',
                          { replace: { country: suggestedCountryNameFormatted } }
          )"
        />

        <ul class="country-selector__prompt-content">
          <li
            v-if="$string('general.country_selector.prompt_currency') !== 'No string found'"
            v-text="$string('general.country_selector.prompt_currency',
                            { replace: { currency: suggestedCurrencyLabel } }
            )"
          />
          <li
            v-if="$string('general.country_selector.prompt_shipping') !== 'No string found'"
            v-text="$string('general.country_selector.prompt_shipping',
                            { replace: { country: suggestedCountryNameFormatted } }
            )"
          />
          <li
            v-if="$string('general.country_selector.prompt_language') !== 'No string found'"
            v-text="$string('general.country_selector.prompt_language',
                            { replace: { language: suggestedLanguageName } }
            )"
          />
        </ul>
      </div>
    </div>

    <!-- Footer -->
    <div
      v-if="!showPrompt"
      class="country-selector__footer"
    >
      <btn
        class="country-selector__button"
        :label="$string('general.country_selector.selector_cta')"
        modifiers="secondary fill block center"
        name="country-selector"
        hover-only
        type="button"
        @click="handleCountrySelectSubmit"
      >
        <template #hoverIcon>
          <icon-right />
        </template>
      </btn>

      <btn
        v-if="!getLocalizedDimissed"
        class="country-selector__button"
        :label="$string('general.country_selector.selector_back')"
        modifiers="secondary block center"
        hover-only
        type="button"
        @click="handleToggleCountrySelector"
      >
        <template #hoverIcon>
          <icon-right />
        </template>
      </btn>
    </div>

    <div
      v-if="showPrompt"
      class="country-selector__footer"
    >
      <btn
        class="country-selector__button"
        :label="$string('general.country_selector.prompt_continue',
                        { replace: { country: currentCountry } }
        )"
        modifiers="secondary fill block center"
        hover-only
        type="button"
        @click="handleCloseOverlay"
      >
        <template #hoverIcon>
          <icon-right />
        </template>
      </btn>

      <btn
        class="country-selector__button"
        :label="$string('general.country_selector.prompt_change_location')"
        modifiers="secondary block center"
        hover-only
        type="button"
        @click="handleToggleCountrySelector"
      >
        <template #hoverIcon>
          <icon-right />
        </template>
      </btn>
    </div>
  </div>
</template>

<script>

/**
 * Vue: Country selector (country-selector)
 * -----------------------------------------------------------------------------
 * Country selector modal.
 *
 * @param {String} liquid.sectionId - Section's Liquid ID.
 *
 */
// eslint-disable-next-line import/no-unresolved
import Cookies from 'js-cookie'
import { mapActions } from 'vuex'

import IconClose from '~icons/directional-navigation/close.svg'
import IconRight from '~icons/directional-navigation/arrow/right.svg'
import Btn from '~global/btn/btn'

import locationizationsQuery from '~graphql/localization.gql'

import { convertToCamelCase, removeTrailingSlash } from '~/helpers/convert'

export default {
  name: 'CountrySelector',

  components: {
    Btn,
    IconClose,
    IconRight,
  },

  props: {
    liquid: {
      type: Object,
      default: () => ({
        sectionId: '',
      }),
    },
  },

  data() {
    return {
      availableLanguages: [],
      currenctCurrencyLabel: `${cnvs.store.currency} ${cnvs.store.currencySymbol}`,
      currentCountry: cnvs.store.country.toLowerCase(),
      currentCountryName: cnvs.store.countryName,
      currentLanguage: cnvs.store.language.toLowerCase(),
      defaultAvailableLanguages: cnvs.strings.defaultLocalization.languages,
      detectedValues: {},
      flagIcon: cnvs.strings.defaultLocalization.flag_svg,
      locales: [],
      localizedShopDomain: cnvs.store.localisedShopDomain,
      shopDomain: cnvs.store.shopDomain,
      showPrompt: null,
      state: 'loading',
      suggestedCountryNameFormatted: '',
      suggestedCountryName: '',
      suggestedCurrencyLabel: '',
      suggestedLanguageName: '',
      updatedLocale: '',
    }
  },

  computed: {

    /**
     * Get the localizedDismissed cookie
     */
    getLocalizedDimissed() {
      return Cookies.get('localizedDismissed')
    },
  },

  watch: {
    state() {
      this.updateAvailableLanguages()
    },

    selectedCountry() {
      this.updateAvailableLanguages()
    },
  },

  beforeMount() {
    this.selectedLanguage = this.currentLanguage
    this.selectedCountry = this.currentCountry
    this.setInitialLocale()
    this.getDetectedBrowsingContext()
    this.handlePrompt()
  },

  async mounted() {
    await this.queryAllLocalizations()
    this.formatSuggestedCountry()
    this.updateAvailableLanguages()
  },

  methods: {

    /**
     * Map Vuex actions.
     */
    ...mapActions({
      closeOverlay: 'overlays/close',
      openOverlay: 'overlays/open',
    }),

    /**
     * Set initial locale
     */
    setInitialLocale() {
      this.cacheLocale = `${this.currentLanguage}-${this.currentCountry}`
    },

    /**
     * Get location from detected browsing context
     */
    getDetectedBrowsingContext() {
      this.detectedValues = cnvs.strings.defaultLocalization.detected_values || false
    },

    /**
     * Format suggested country info
     */
    formatSuggestedCountry() {
      if (!this.detectedValues) {
        return
      }

      this.suggestedCountryName = this.detectedValues.country.name
      const united = this.$string('general.country_selector.prompt_country_united').toLowerCase()

      if (this.detectedValues.country.name.toLowerCase().includes(united)) {
        const prefix = this.$string('general.country_selector.prompt_country_prefix_united')
          ? `${this.$string('general.country_selector.prompt_country_prefix_united')} `
          : ''
        this.suggestedCountryNameFormatted = `${prefix}${this.detectedValues.country.name}`
      } else {
        this.suggestedCountryNameFormatted = this.detectedValues.country.name
      }

      const suggested = this.locales.find((country) => {
        return country.isoCode === this.detectedValues.country.handle
      })

      this.suggestedCurrencyLabel = `${suggested.currency.isoCode} ${suggested.currency.symbol}`
      this.suggestedLanguageName = suggested.availableLanguages[0].endonymName
      this.selectedCountry = this.detectedValues.country.handle
    },

    /**
     * Handle the country selector prompt
     */
    handlePrompt() {
      const localizedDismissed = Cookies.get('localizedDismissed')
      if (!localizedDismissed) {
        this.showPrompt = true
        return
      }

      const cookieSplit = localizedDismissed.split('-')
      const cookieDomain = cookieSplit[1]

      if (!this.detectedValues || cookieDomain === this.currentCountry) {
        this.showPrompt = false
        return
      }

      const browsingContextHandle = this.detectedValues.country.handle || false

      if (!browsingContextHandle) {
        this.showPrompt = false
        return
      }

      this.showPrompt = browsingContextHandle !== cnvs.store.country
    },

    /**
     * Handle close overlay
     */
    handleCloseOverlay() {
      this.closeOverlay('countrySelector')
      this.handleDismissLocalizationPrompt()
    },

    /**
     * Handle dismiss prompt
     */
    handleDismissLocalizationPrompt() {
      Cookies.set('localizedDismissed', this.cacheLocale, { expires: 365 })
      this.handlePrompt()
    },

    /**
     * Handle toggle between prompt and country selector
     */
    handleToggleCountrySelector() {
      this.showPrompt = !this.showPrompt
    },

    /**
     * Handle coutnry selector cta.
     * @param {Object} event - click event
     */
    handleCountrySelectSubmit() {
      const primary = cnvs.strings.defaultLocalization.primaryMarket
      const newCountryCode = `-${this.selectedCountry}`.toLowerCase()
      const newLanguageCode = this.selectedLanguage.toLowerCase() === 'pt_pt'
        ? this.selectedLanguage.toLowerCase().replace('_pt', '')
        : this.selectedLanguage.toLowerCase()

      const currentCountry = `-${this.currentCountry}`

      const currentLocale = `${this.currentLanguage}${currentCountry}`
      this.updatedLocale = `${newLanguageCode}${newCountryCode}`
      this.cacheLocale = this.updatedLocale

      const isPrimaryDomain = primary.domain.toLowerCase() === this.selectedCountry.toLowerCase()
      const isPrimaryLocale = primary.locale.toLowerCase() === newLanguageCode

      if (currentLocale !== this.updatedLocale) {
        if (isPrimaryDomain) {
          this.updatedLocale = this.updatedLocale.replace(newCountryCode, '')

          if (isPrimaryLocale) {
            this.updatedLocale = this.updatedLocale.replace(newLanguageCode, '')
          }
        }

        this.updateLocationUrl()
      }

      this.handleCloseOverlay()
    },

    /**
     * Update location url with locale subfolder path
     */
    updateLocationUrl() {
      const newShopDomain = removeTrailingSlash(`${this.shopDomain}${this.updatedLocale}`)
      const newUrl = window.location.href
        .replace(removeTrailingSlash(this.localizedShopDomain), newShopDomain)

      window.location = newUrl
    },

    /**
     * Query all localization data with graphql
     */
    queryAllLocalizations() {
      return new Promise(async(resolve, reject) => {
        try {
          const response = await cnvs.Query({
            query: locationizationsQuery,
          })

          this.locales = response.localization.availableCountries
          this.state = 'ready'
          resolve()

        } catch (error) {
          cnvs.ReportError('Failed to load localizations', error)
          reject(error)
        }
      })
    },

    /**
     * Handle locale selector
     * @param {Object} event - data from select option change
     */
    handleLocaleSelect(event) {
      const selectedLocale = convertToCamelCase(event.target.name.replace('_', ' '))
      this[selectedLocale] = event.target.value

      if (selectedLocale === 'selectedCountry') {
        this.updateAvailableLanguages()
      }
    },

    /**
     * Update available language options
     */
    updateAvailableLanguages() {
      const languages = this.locales.find((country) => {
        return country.isoCode.toLowerCase() === this.selectedCountry.toLowerCase()
      })

      if (!languages) {
        return
      }
      this.availableLanguages = languages.availableLanguages

      const hasSelectedLanguage = this.availableLanguages.some((lang) => {
        return lang.isoCode.toLowerCase() === this.selectedLanguage.toLowerCase()
      })

      if (hasSelectedLanguage) {
        return
      }

      this.selectedLanguage = this.availableLanguages[0].isoCode
    },

    /**
     * Update selected option state.
     * @param {String} current - current option
     * @param {String} selected - selected option
     * @returns {Boolean}
     */
    getSelectedState(current, selected) {
      return current === selected
    },
  },
}
</script>

<style lang="scss">
@import './country-selector';
</style>
